import ApplicationController from '../../../../javascript/controllers/application_controller'

/**
 * Main Info Controller
 *
 * Manages dynamic field reordering based on selected publishable type.
 * Tracks publishable type changes via breadcrumbs.
 *
 * This Stimulus controller facilitates the dynamic re-ordering of form fields
 * based on the selected publishable type. Primary and secondary fields are defined
 * for each publishable type and are stored in data attributes on the controller element.
 * primaryFields holds, per type, a list of lines of { target, span }; secondaryFields a flat list of
 * { target, span }. The server decides the order and every width; this controller only moves fields.
 *
 * Breadcrumbs:
 *   - Form/publishable_type_changed: When user changes type dropdown
 *
 * Metadata: from (old type), to (new type), fieldCount
 *
 * Usage:
 * - Define the primary fields for each publishable type in the primaryFields value.
 * - Specify the secondary fields for each publishable type in the secondaryFields value.
 * - Assign the primaryContainer and secondaryContainer targets to the containers
 * - Assign the line target to each line of the primary container that a type switch replaces,
 *   and the lineTemplate target to a <template> holding an empty line to clone
 * - Assign the field targets to the fields
 *
 * @extends ApplicationController
 */
export default class extends ApplicationController {
  static targets = [
    'primaryContainer',
    'secondaryContainer',
    'line',
    'lineTemplate',

    // Publication Types
    'articleContainer',
    'bookContainer',
    'miscellaneousContainer',
    'patentContainer',
    'periodicalContainer',
    'proceedingsContainer',
    'reportContainer',
    'standardContainer',
    'thesisContainer',
    'unknownContainer',

    // Fields
    'abstractField',
    'addressField',
    'authorsField',
    'doiField',
    'editionField',
    'editorsField',
    'identifiersField',
    'isbnField',
    'issnField',
    'journalField',
    'languageField',
    'numberField',
    'organizationField',
    'pagesField',
    'parentPublicationField',
    'pmidField',
    'arxivIdField',
    'publishableTypeField',
    'publishedOnField',
    'publisherField',
    'repositoryIdField',
    'seriesField',
    'standardKindField',
    'standardPredecessorsField',
    'standardStatusField',
    'standardSuccessorsField',
    'subtitleField',
    'titleField',
    'uniformTitleField',
    'urlField',
    'volumeField',
  ]

  static values = {
    secondaryFields: Object,
    primaryFields: Object,
    publishableTypeSpecificFields: Object,
  }

  connect() {
    if (this.hasPublishableTypeFieldTarget) {
      // Initialize lastPublishableType on connect
      this.lastPublishableType = this.publishableTypeFieldTarget.querySelector('select').value
      this.updateFields()
    }
  }

  updateFields() {
    const publishableType = this.publishableTypeFieldTarget.querySelector('select').value
    const primaryFieldsForType = this.primaryFieldsValue[publishableType]
    const secondaryFieldsForType = this.secondaryFieldsValue[publishableType]

    // Track the change (capture old value before DOM changes)
    if (this.lastPublishableType !== publishableType) {
      this.addBreadcrumb('Form', 'publishable_type_changed', {
        from: this.lastPublishableType,
        to: publishableType,
        fieldCount: primaryFieldsForType.flat().length,
      })
    }
    this.lastPublishableType = publishableType

    // Resolve every primary field and the line template before moving anything, so a missing
    // target throws while the form is still intact
    const primaryLines = primaryFieldsForType.map((line) =>
      line.map(({ target, span }) => ({ element: this[`${target}Target`], span })),
    )
    const lineTemplate = this.lineTemplateTarget.content.firstElementChild

    // First, move all publication type specific fields to their respective containers
    Object.entries(this.publishableTypeSpecificFieldsValue).forEach(([key, fields]) => {
      const container = this[`${this.camelize(key)}ContainerTarget`]

      fields.forEach((field) => {
        container.appendChild(this[`${field}Target`])
      })
    })

    // Second, move primary fields into new line elements, one per line
    const newLines = primaryLines.map((line) => {
      const lineElement = lineTemplate.cloneNode()
      line.forEach(({ element, span }) => this.place(lineElement, element, span))
      return lineElement
    })

    // Third, place the remaining fields as secondary fields in order
    secondaryFieldsForType.forEach(({ target, span }) => {
      const hasMethod = `${this.camelize(`has-${target}`)}Target`
      if (this[hasMethod]) {
        this.place(this.secondaryContainerTarget, this[`${target}Target`], span)
      }
    })

    // Fourth, swap the emptied lines for the new ones, after the static lines
    this.lineTargets.forEach((line) => line.remove())
    this.primaryContainerTarget.append(...newLines)
  }

  /**
   * Append a field to a container and give it the width class of its line.
   *
   * @param {HTMLElement} container Container to append to
   * @param {HTMLElement} element Field cell to move
   * @param {string} span Width class of the field's line
   */
  place(container, element, span) {
    const spans = [...element.classList].filter((name) => name.startsWith('lg:col-span-'))
    element.classList.remove(...spans)
    element.classList.add(span)
    container.appendChild(element)
  }

  /**
   * Camelize a string, cutting the string by multiple separators like
   * hyphens, underscores and spaces.
   *
   * @param {text} string Text to camelize
   * @return string Camelized text
   */
  camelize(text) {
    return text.replace(/^([A-Z])|[\s-_]+(\w)/g, function (match, p1, p2, offset) {
      if (p2) return p2.toUpperCase()
      return p1.toLowerCase()
    })
  }
}
